import { Component, Input, Output, EventEmitter, AfterViewInit, OnChanges, SimpleChanges, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

export type ConfirmationType = 'delete' | 'revoke' | 'warning' | 'info' | 'success';
export type ThemeColor = 'red' | 'green' | 'orange' | 'blue';

@Component({
  selector: 'app-confirmation-dialog',
  standalone: true,
  imports: [CommonModule, DialogModule, ButtonModule],
  templateUrl: './confirmation-dialog.component.html',
  styleUrl: './confirmation-dialog.component.scss',
})
export class ConfirmationDialogComponent implements AfterViewInit, OnChanges {
  @Input() visible: boolean = false;
  @Input() type: ConfirmationType = 'warning';
  @Input() title: string = '';
  @Input() message: string = '';
  @Input() confirmLabel: string = 'Confirm';
  @Input() cancelLabel: string = 'Cancel';
  @Input() themeColor: ThemeColor = 'red';
  @Input() width: string = '540px';
  @Input() styleClass: string = 'confirmation-dialog';
  @Input() onLinkClick?: (linkType: 'terms' | 'privacy') => void;
  @Input() showCancelButton: boolean = true;
  @Input() nextStepsTitle: string = '';
  @Input() nextStepsMessage: string = '';
  @Input() loading: boolean = false;
  @Input() secondaryActionLabel: string = '';
  @Input() showSecondaryAction: boolean = false;
  @Input() confirmDisabled: boolean = false;
  @Input() closable: boolean = true;

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() onConfirm = new EventEmitter<void>();
  @Output() onCancel = new EventEmitter<void>();
  @Output() onSecondaryAction = new EventEmitter<void>();
  @Output() onClose = new EventEmitter<void>();

  @ViewChild('messageContainer', { static: false }) messageContainer?: ElementRef;

  safeMessage: SafeHtml = '';

  constructor(private sanitizer: DomSanitizer) {}

  ngAfterViewInit(): void {
    this.updateMessage();
    this.setupLinkHandlers();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['message']) {
      this.updateMessage();
      if (this.messageContainer) {
        setTimeout(() => this.setupLinkHandlers(), 0);
      }
    }
  }

  updateMessage(): void {
    // Replace link placeholders with proper HTML
    let processedMessage = this.message;
    processedMessage = processedMessage.replace(
      /<a href="#" class="confirmation-link">Terms of Service<\/a>/g,
      '<a href="#" class="confirmation-link" data-link-type="terms">Terms of Service</a>'
    );
    processedMessage = processedMessage.replace(
      /<a href="#" class="confirmation-link">Privacy Policy<\/a>/g,
      '<a href="#" class="confirmation-link" data-link-type="privacy">Privacy Policy</a>'
    );
    this.safeMessage = this.sanitizer.bypassSecurityTrustHtml(processedMessage);
  }

  setupLinkHandlers(): void {
    if (this.messageContainer && this.onLinkClick) {
      const links = this.messageContainer.nativeElement.querySelectorAll('.confirmation-link');
      links.forEach((link: HTMLElement) => {
        link.addEventListener('click', (e: Event) => {
          e.preventDefault();
          const linkType = (link as HTMLElement).getAttribute('data-link-type') as 'terms' | 'privacy';
          if (linkType && this.onLinkClick) {
            this.onLinkClick(linkType);
          }
        });
      });
    }
  }

  getIconColor(): string {
    switch (this.themeColor) {
      case 'red':
        return '#dc2626';
      case 'green':
        return '#16a34a';
      case 'orange':
        return '#f59e0b';
      case 'blue':
        return '#0b6c92';
      default:
        return '#dc2626';
    }
  }

  getButtonColor(): string {
    switch (this.themeColor) {
      case 'red':
        return '#dc2626';
      case 'green':
        return '#16a34a';
      case 'orange':
        return '#f59e0b';
      case 'blue':
        return '#0b6c92';
      default:
        return '#dc2626';
    }
  }

  getButtonHoverColor(): string {
    switch (this.themeColor) {
      case 'red':
        return '#b91c1c';
      case 'green':
        return '#15803d';
      case 'orange':
        return '#d97706';
      case 'blue':
        return '#094d6b';
      default:
        return '#b91c1c';
    }
  }

  getIconBgColor(): string {
    switch (this.themeColor) {
      case 'red':
        return '#fee2e2';
      case 'green':
        return '#d1fae5';
      case 'orange':
        return '#fef3c7';
      case 'blue':
        return '#dbeeff';
      default:
        return '#fee2e2';
    }
  }

  getNextStepsBgColor(): string {
    switch (this.themeColor) {
      case 'green':
        return '#d1fae5';
      case 'blue':
        return '#dbeeff';
      default:
        return '#d1fae5';
    }
  }

  getNextStepsAccentColor(): string {
    switch (this.themeColor) {
      case 'green':
        return '#15803d';
      case 'blue':
        return '#0b6c92';
      default:
        return '#15803d';
    }
  }

  handleVisibleChange(visible: boolean): void {
    this.visibleChange.emit(visible);
    
    if (!visible) {
      this.onClose.emit();
    }
  }

  handleConfirm(): void {
    if (this.loading) {
      return; // Prevent action if already loading
    }
    this.onConfirm.emit();
    // Don't close dialog automatically - parent component will manage closing
    // This allows parent to handle async operations and show loading states properly
  }

  handleCancel(): void {
    this.onCancel.emit();
    this.handleVisibleChange(false);
  }

  handleSecondaryAction(): void {
    if (this.loading) {
      return; // Prevent action if already loading
    }
    this.onSecondaryAction.emit();
    // Don't close dialog automatically - parent component will manage closing
  }
}

